// One action makes the server push several live events (a speed-up: system state, building done,
// map changed, inbox notice, Dark Matter). Reloading on each one hit the per-account request limit,
// so reloads are collected for a short quiet window and each query reloads once.
import type { QueryClient, QueryKey } from '@tanstack/react-query';

/** Quiet time after the last request before reloading. */
export const REFETCH_COALESCE_MS = 250;
/** A steady stream of events (battle rounds) still reloads at least this often. */
const MAX_WAIT_MS = 1000;

interface Pending {
  keys: Map<string, QueryKey>;
  timer: ReturnType<typeof setTimeout> | undefined;
  firstAt: number;
}

const pendingByClient = new WeakMap<Pick<QueryClient, 'invalidateQueries'>, Pending>();

export function invalidateSoon(
  queryClient: Pick<QueryClient, 'invalidateQueries'>,
  keys: readonly QueryKey[]
): void {
  let pending = pendingByClient.get(queryClient);
  if (!pending) {
    pending = { keys: new Map(), timer: undefined, firstAt: Date.now() };
    pendingByClient.set(queryClient, pending);
  }
  for (const key of keys) pending.keys.set(JSON.stringify(key), key);
  clearTimeout(pending.timer);
  const wait = Math.min(
    REFETCH_COALESCE_MS,
    Math.max(0, pending.firstAt + MAX_WAIT_MS - Date.now())
  );
  pending.timer = setTimeout(() => {
    pendingByClient.delete(queryClient);
    for (const key of pending.keys.values()) void queryClient.invalidateQueries({ queryKey: key });
  }, wait);
}
